import {
	SalesChannelCategoryGroupProductsDocument,
	type SalesChannelCategoryGroupProductsQuery,
	type SalesChannelCategoryGroupProductsQueryVariables,
} from "@/generated/graphql";
import { useCallback, useEffect, useRef, useState } from "react";
import { type CombinedError, useClient } from "urql";

export interface SalesChannelCategoryGroupProductRow {
	salesChannelCategoryGroupProductID: string;
	salesChannelCategoryGroupID: string;
	salesChannelCategoryID: string;
	productID: string;
	productCode: string;
	productName: string;
	barcode: string;
	displayOrder: number;
	isActive: boolean;
	priceType: string;
	customPrice: number;
	imageType: string;
	externalImageURL: string;
	qty?: number | null;
	// Live availability from the server (branch-scoped); replaces the cron out_of_stock list in the tree.
	isOutOfStock: boolean;
	// Branch-scoped: the product's resolved channel price is 0, so it cannot go live on the channel.
	isNoPrice: boolean;
	product?: {
		productID: string;
		productCode: string;
		productName: string;
		barcode: string;
		isActive: boolean;
		canBeSold: boolean;
		mainImageFileMediumPathURL?: string | null;
		hasAddon: boolean;
		addonGroupID?: string | null;
		addonGroup?: { productAddonGroupID: string; name: string } | null;
		baseUom?: { uomID: string; uomName: string } | null;
	} | null;
}

// A single 500-row request keeps the tree empty until the slowest stock lookup in it
// returns, so pages are fetched in chunks and rendered as they land.
const PAGE_SIZE = 100;

export const useSalesChannelCategoryGroupProductList = (
	groupID: string | undefined,
	branchID?: string,
) => {
	const client = useClient();
	const [items, setItems] = useState<SalesChannelCategoryGroupProductRow[]>([]);
	const [fetching, setFetching] = useState(Boolean(groupID));
	const [error, setError] = useState<CombinedError | undefined>();
	const requestRef = useRef(0);

	const load = useCallback(async () => {
		if (!groupID) {
			setItems([]);
			setFetching(false);
			return;
		}

		// A branch switch mid-flight must not let the old pages append to the new list.
		requestRef.current += 1;
		const requestID = requestRef.current;
		const isStale = () => requestRef.current !== requestID;

		setFetching(true);
		setError(undefined);
		setItems([]);

		const accumulated: SalesChannelCategoryGroupProductRow[] = [];
		let page = 1;

		try {
			while (true) {
				const { data, error: pageError } = await client
					.query<
						SalesChannelCategoryGroupProductsQuery,
						SalesChannelCategoryGroupProductsQueryVariables
					>(
						SalesChannelCategoryGroupProductsDocument,
						{
							qop: {
								pagination: { page, pageSize: PAGE_SIZE, withCount: false },
								sorts: [{ field: "display_order", direction: "asc" }],
								filters: {
									salesChannelCategoryGroupID: groupID,
									// Stock is only resolved when a branch is named; the group is required with it.
									branchID: branchID || null,
								},
							},
						},
						{ requestPolicy: "network-only", suspense: false },
					)
					.toPromise();

				if (isStale()) return;
				if (pageError) throw pageError;

				const result = data?.salesChannelCategoryGroupProducts;
				const pageItems = result?.items ?? [];
				accumulated.push(...pageItems);
				// Showing each page as it lands beats holding a spinner for the whole group.
				setItems([...accumulated]);

				if (!result?.pagination?.hasNext || !pageItems.length) break;
				page += 1;
			}
		} catch (caught) {
			if (!isStale()) setError(caught as CombinedError);
		} finally {
			if (!isStale()) setFetching(false);
		}
	}, [client, groupID, branchID]);

	useEffect(() => {
		load();
	}, [load]);

	return { items, fetching, error, refetch: load };
};
